import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiError(err) {
  // Accept either a raw axios error OR just the `detail` value (backwards-compatible).
  const detail = err && err.isAxiosError ? err.response?.data?.detail : err;
  if (err && err.isAxiosError && !err.response) {
    // Request never got a response (network / CORS / offline / extension block).
    return "Can't reach the server. Please check your connection and try again.";
  }
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fileUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  if (pathOrUrl.startsWith("/api/")) return `${BACKEND_URL}${pathOrUrl}`;
  return pathOrUrl;
}

// Brand-coloured initials avatar — used when a profile has no uploaded photo.
// Returns an inline SVG data-URI (no external request, gender-agnostic, elegant).
const AVATAR_PALETTES = [
  { bg: "#7A1C28", fg: "#F5E8C9" }, // maroon / cream
  { bg: "#C5A059", fg: "#3B2417" }, // gold / dark
  { bg: "#3B2417", fg: "#E9CFA3" }, // espresso / sand
  { bg: "#A55C4B", fg: "#FAF8F5" }, // rust / warm white
  { bg: "#5C3A2E", fg: "#E6C58B" }, // walnut / honey
];

function initialsOf(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function paletteFor(seed) {
  const str = String(seed || "").trim() || "?";
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTES[h % AVATAR_PALETTES.length];
}

export function defaultAvatar(name, size = 320) {
  const initials = initialsOf(name);
  const { bg, fg } = paletteFor(name);
  const fontSize = Math.round(size * 0.42);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
      `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
        `<stop offset="0%" stop-color="${bg}"/>` +
        `<stop offset="100%" stop-color="${bg}" stop-opacity="0.78"/>` +
      `</linearGradient></defs>` +
      `<rect width="100%" height="100%" fill="url(#g)"/>` +
      `<text x="50%" y="50%" dy=".34em" text-anchor="middle" ` +
        `font-family="'Cormorant Garamond', Georgia, serif" font-weight="600" ` +
        `font-size="${fontSize}" fill="${fg}" letter-spacing="2">${initials}</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// Returns a profile photo URL, falling back to an initials avatar if none is set.
export function avatarUrl(profile, size = 320) {
  const direct = fileUrl(profile?.photo_url);
  if (direct) return direct;
  return defaultAvatar(profile?.name || profile?.user_id, size);
}
